// "Métricas" en `/instagram`: cómo le va a la cuenta y a cada post —
// ADR-2026-10-29-dashboard-de-metricas-de-instagram.
//
// Arriba, cinco tarjetas como las del calendario (seguidores, alcance, vistas, interacciones y
// posts del período, comparados con el período anterior). Abajo, los posts como el perfil de
// Instagram; al tocar uno, su detalle. Lee en vivo de la edge `ig-metricas` (la misma del chat):
// todavía no hay barrido que guarde la historia, así que no hay evolución día a día.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Eye, Film, Heart, Instagram, Layers, Loader2, RefreshCw, Sparkles, Users } from 'lucide-react';
import {
  leerCuentaDeInstagram,
  leerPostsDeInstagram,
  type CuentaDeInstagram,
  type Lectura,
  type PostDeInstagram,
} from '../../../../lib/instagramMetricas';
import { useRegistrarPantalla } from '../../../../chat-atlas';
import { boton } from '../../botones';
import { postsDeAtlasPorMedia } from '../postsApi';
import { DetalleDePostDeInstagram } from './DetalleDePostDeInstagram';
import { MiniaturaDeInstagram } from './MiniaturaDeInstagram';
import { SelectorSegmentado } from './SelectorSegmentado';
import {
  formatearNumero,
  haceCuanto,
  instanteDe,
  numeroPrincipal,
  ordenarPosts,
  ORDENES,
  PERIODOS,
  sePuedeActualizar,
  tarjetasDeLaCuenta,
  textoDeVariacion,
  type Orden,
  type Periodo,
  type Tarjeta,
} from './metricas';

const NO_SE_PUDO = 'No pudimos leer tus números de Instagram. Revisá la conexión y probá de nuevo.';

/** Una lectura en curso, la que llegó, o la falla de red (el mensaje). */
type Estado<T> = { cargando: true } | { cargando: false; lectura: Lectura<T>; leidoEn: number } | { cargando: false; falla: string };

export function MetricasDeInstagram({
  brandId,
  companyId,
  lanzamientoId,
  onAbrirEnCalendario,
}: {
  brandId: string | null;
  companyId: string;
  /** El calendario de la marca: para reconocer los posts que salieron de Atlas. */
  lanzamientoId: string;
  onAbrirEnCalendario: (postId: string) => void;
}) {
  const navigate = useNavigate();
  const [dias, setDias] = useState<Periodo>(28);
  const [orden, setOrden] = useState<Orden>('recientes');
  const [cuenta, setCuenta] = useState<Estado<CuentaDeInstagram>>({ cargando: true });
  const [posts, setPosts] = useState<Estado<PostDeInstagram[]>>({ cargando: true });
  const [abierto, setAbierto] = useState<string | null>(null);
  const [desdeAtlas, setDesdeAtlas] = useState<Map<string, string>>(new Map());
  /** El último "Actualizar": cambiar de período no resetea la espera. */
  const [actualizadoAMano, setActualizadoAMano] = useState<number | null>(null);
  // "Actualizado hace X" se recalcula solo cada minuto.
  const [, setTic] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setTic((n) => n + 1), 60_000);
    return () => window.clearInterval(t);
  }, []);

  // El último pedido de cada cosa: si cambia la marca o el período con uno en vuelo, el viejo no pisa.
  const pedidoCuenta = useRef(0);
  const pedidoPosts = useRef(0);

  const cargarCuenta = useCallback(
    async (forzar: boolean) => {
      const n = ++pedidoCuenta.current;
      setCuenta({ cargando: true });
      try {
        const r = await leerCuentaDeInstagram(brandId, dias, { forzar });
        if (n === pedidoCuenta.current) setCuenta({ cargando: false, ...r });
      } catch {
        if (n === pedidoCuenta.current) setCuenta({ cargando: false, falla: NO_SE_PUDO });
      }
    },
    [brandId, dias],
  );

  const cargarPosts = useCallback(
    async (forzar: boolean) => {
      const n = ++pedidoPosts.current;
      setPosts({ cargando: true });
      try {
        const r = await leerPostsDeInstagram(brandId, { forzar });
        if (n !== pedidoPosts.current) return;
        setPosts({ cargando: false, ...r });
        if (r.lectura.tipo === 'ok') {
          const atlas = await postsDeAtlasPorMedia(lanzamientoId, companyId, r.lectura.datos.map((p) => p.id));
          if (n === pedidoPosts.current) setDesdeAtlas(atlas);
        }
      } catch {
        if (n === pedidoPosts.current) setPosts({ cargando: false, falla: NO_SE_PUDO });
      }
    },
    [brandId, lanzamientoId, companyId],
  );

  useEffect(() => {
    void cargarCuenta(false);
  }, [cargarCuenta]);
  useEffect(() => {
    setAbierto(null);
    setDesdeAtlas(new Map());
    void cargarPosts(false);
  }, [cargarPosts]);

  const lecturaCuenta = 'lectura' in cuenta ? cuenta.lectura : null;
  const lecturaPosts = 'lectura' in posts ? posts.lectura : null;
  const listaDePosts = lecturaPosts?.tipo === 'ok' ? lecturaPosts.datos : null;
  const ordenados = useMemo(() => (listaDePosts ? ordenarPosts(listaDePosts, orden) : []), [listaDePosts, orden]);
  const postAbierto = listaDePosts?.find((p) => p.id === abierto) ?? null;
  const usuario =
    (lecturaCuenta?.tipo === 'ok' && lecturaCuenta.usuario) || (lecturaPosts?.tipo === 'ok' && lecturaPosts.usuario) || null;

  const leidoEn = [cuenta, posts]
    .map((e) => ('leidoEn' in e ? e.leidoEn : null))
    .filter((x): x is number => x !== null);
  const masViejo = leidoEn.length ? Math.min(...leidoEn) : null;
  // La espera cuenta desde lo último que se le preguntó a Instagram, sea a mano o al abrir.
  const ultimaLectura = Math.max(actualizadoAMano ?? 0, ...leidoEn) || null;
  const cargando = cuenta.cargando || posts.cargando;

  // El agente sabe qué está mirando la persona. Desde acá no tiene las herramientas del
  // calendario (son de esa pantalla): para planificar, la manda de vuelta a "Calendario".
  useRegistrarPantalla({
    clave: 'metricas-instagram',
    describir: () => ({
      pantalla: 'las métricas de Instagram',
      ruta: '/instagram?ver=metricas',
      queHace: 'Muestra cómo le va a la cuenta de Instagram de la marca y a cada post. Sólo lectura.',
      campos: [],
      faltaParaGenerar: [],
      estado: [
        `Mirando los últimos ${dias} días${usuario ? ` de @${usuario}` : ''}.`,
        postAbierto ? `Abierto el detalle del post ${postAbierto.id} (${postAbierto.link ?? 'sin link'}).` : '',
      ]
        .filter(Boolean)
        .join(' '),
      notas: [
        'Para leer números usá tus herramientas de Instagram; son los mismos que ve la persona.',
        'Para planificar, crear o mover posts, la persona tiene que pasar a «Calendario» (arriba a la derecha): ahí recibís las herramientas del calendario.',
      ],
    }),
    aplicar: () => ({ aplicados: [], fallidos: [] }),
  });

  // Lo que le pasa a la CUENTA tapa todo: sin conexión o vencida no hay nada que mostrar.
  const deLaCuenta = [lecturaCuenta, lecturaPosts].find((l) => l?.tipo === 'sin_conexion' || l?.tipo === 'vencida');
  if (deLaCuenta?.tipo === 'sin_conexion') {
    return (
      <Vacio
        titulo="Conectá tu Instagram"
        texto="Vas a ver cuántas personas te alcanzan, tus seguidores y cómo le fue a cada post. Es gratis y Atlas sólo lee: no toca tu cuenta."
        accion={{ texto: 'Conectar en Mi marca', onClick: () => navigate('/mi-marca') }}
      />
    );
  }
  if (deLaCuenta?.tipo === 'vencida') {
    return (
      <Vacio
        titulo="Volvé a conectar tu Instagram"
        texto={deLaCuenta.mensaje}
        accion={{ texto: 'Ir a Mi marca', onClick: () => navigate('/mi-marca') }}
      />
    );
  }

  return (
    <div className="mt-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <SelectorSegmentado
            etiqueta="Período"
            opciones={PERIODOS.map((d) => ({ valor: d, texto: `${d} días` }))}
            valor={dias}
            onCambiar={setDias}
          />
          {usuario && <span className="hidden truncate text-sm text-neutral-500 sm:inline">@{usuario}</span>}
        </div>
        <div className="flex items-center gap-2">
          {masViejo !== null && !cargando && (
            <span className="text-xs text-neutral-500">Actualizado {haceCuanto(masViejo)}</span>
          )}
          <button
            type="button"
            disabled={cargando || !sePuedeActualizar(ultimaLectura)}
            onClick={() => {
              setActualizadoAMano(Date.now());
              void cargarCuenta(true);
              void cargarPosts(true);
            }}
            title={sePuedeActualizar(ultimaLectura) ? undefined : 'Recién actualizado: probá en unos minutos'}
            className={boton('secundario', { chico: true, extra: 'py-2' })}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${cargando ? 'animate-spin' : ''}`} /> Actualizar
          </button>
        </div>
      </div>

      {/* ── La cuenta ── */}
      {cuenta.cargando ? (
        <TarjetasCargando />
      ) : 'falla' in cuenta ? (
        <Aviso texto={cuenta.falla} onReintentar={() => void cargarCuenta(true)} />
      ) : cuenta.lectura.tipo === 'ok' ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 animate-ficha-entra lg:grid-cols-5">
            {tarjetasDeLaCuenta(cuenta.lectura.datos, listaDePosts).map((t) => (
              <TarjetaDeNumero key={t.clave} tarjeta={t} esperando={t.clave === 'posts' && posts.cargando} />
            ))}
          </div>
          <p className="mt-2 text-xs text-neutral-500">
            Comparado con los {dias} días anteriores. Instagram tarda hasta 48 h en completar los números.
          </p>
        </>
      ) : cuenta.lectura.tipo === 'no_disponible' ? (
        cuenta.lectura.sinPermiso ? (
          <Aviso texto={cuenta.lectura.mensaje} accion={{ texto: 'Ir a Mi marca', onClick: () => navigate('/mi-marca') }} />
        ) : (
          <Aviso texto={cuenta.lectura.mensaje} onReintentar={() => void cargarCuenta(true)} />
        )
      ) : null}

      {/* ── Los posts ── */}
      <div className="mt-8 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-brand-black">Tus posts</h2>
        {listaDePosts && listaDePosts.length > 1 && (
          <SelectorSegmentado
            etiqueta="Ordenar posts"
            opciones={ORDENES.map((o) => ({ valor: o.clave, texto: o.etiqueta }))}
            valor={orden}
            onCambiar={setOrden}
          />
        )}
      </div>
      {posts.cargando ? (
        <GrillaCargando />
      ) : 'falla' in posts ? (
        <Aviso texto={posts.falla} onReintentar={() => void cargarPosts(true)} />
      ) : posts.lectura.tipo === 'no_disponible' ? (
        <Aviso texto={posts.lectura.mensaje} onReintentar={() => void cargarPosts(true)} />
      ) : ordenados.length === 0 ? (
        <p className="mt-3 rounded-2xl border border-neutral-200 bg-white py-10 px-6 text-center text-sm text-neutral-500">
          Todavía no hay posts en {usuario ? `@${usuario}` : 'tu cuenta'}. Cuando publiques, aparecen acá con sus números.
        </p>
      ) : (
        <>
          <div className="mt-3 grid grid-cols-3 gap-1 animate-ficha-entra sm:gap-2 md:grid-cols-4 lg:grid-cols-5">
            {ordenados.map((p) => (
              <CasillaDePost key={p.id} post={p} desdeAtlas={desdeAtlas.has(p.id)} onAbrir={() => setAbierto(p.id)} />
            ))}
          </div>
          <p className="mt-2 text-xs text-neutral-500">
            Los últimos {ordenados.length} posts. Las historias no aparecen: duran 24 h.
          </p>
        </>
      )}

      <DetalleDePostDeInstagram
        post={postAbierto}
        desdeAtlas={postAbierto ? desdeAtlas.has(postAbierto.id) : false}
        onAbrirEnCalendario={() => {
          const id = postAbierto ? desdeAtlas.get(postAbierto.id) : undefined;
          if (id) onAbrirEnCalendario(id);
        }}
        onCerrar={() => setAbierto(null)}
      />
    </div>
  );
}

// ── Piezas ───────────────────────────────────────────────────────────────────

function TarjetaDeNumero({ tarjeta, esperando }: { tarjeta: Tarjeta; esperando: boolean }) {
  const variacion = textoDeVariacion(tarjeta.variacion);
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white py-3 px-4 shadow-sm">
      <p className="truncate text-[11px] font-medium uppercase tracking-wide text-neutral-500">{tarjeta.etiqueta}</p>
      <p className="mt-1 flex items-baseline gap-2">
        {esperando ? (
          <Loader2 className="my-1.5 w-4 h-4 animate-spin text-neutral-300" />
        ) : (
          <span className="text-xl font-semibold text-brand-black">{formatearNumero(tarjeta.valor)}</span>
        )}
        {variacion && (
          <span
            className={`text-xs font-medium ${
              (tarjeta.variacion ?? 0) > 0 ? 'text-emerald-600' : (tarjeta.variacion ?? 0) < 0 ? 'text-red-600' : 'text-neutral-500'
            }`}
          >
            {variacion}
          </span>
        )}
      </p>
      {tarjeta.detalle && <p className="mt-0.5 truncate text-xs text-neutral-500">{tarjeta.detalle}</p>}
    </div>
  );
}

function CasillaDePost({ post, desdeAtlas, onAbrir }: { post: PostDeInstagram; desdeAtlas: boolean; onAbrir: () => void }) {
  const { valor, etiqueta } = numeroPrincipal(post);
  const Icono = etiqueta === 'vistas' ? Eye : etiqueta === 'me gusta' ? Heart : Users;
  return (
    <button
      type="button"
      onClick={onAbrir}
      aria-label={`Ver los números del post${
        Number.isFinite(instanteDe(post.fecha)) ? ` del ${new Date(instanteDe(post.fecha)).toLocaleDateString('es-AR')}` : ''
      }`}
      className="group relative aspect-square overflow-hidden rounded-md bg-neutral-100 transition-transform duration-150 ease-out motion-safe:active:scale-[0.96]"
    >
      <MiniaturaDeInstagram src={post.miniatura} className="h-full w-full transition-opacity group-hover:opacity-90" />
      {(post.tipo === 'reel' || post.formato === 'carrusel') && (
        <span className="absolute right-1.5 top-1.5 grid h-6 w-6 place-items-center rounded-md bg-black/35 text-white">
          {post.tipo === 'reel' ? <Film className="w-3.5 h-3.5" /> : <Layers className="w-3.5 h-3.5" />}
        </span>
      )}
      {desdeAtlas && (
        <span
          title="Publicado desde Atlas"
          className="absolute left-1.5 top-1.5 grid h-5 w-5 place-items-center rounded-full bg-white/90 text-brand-primary shadow-sm"
        >
          <Sparkles className="w-3 h-3" />
        </span>
      )}
      {valor !== null && (
        <span className="absolute inset-x-0 bottom-0 flex items-center gap-1 bg-gradient-to-t from-black/60 to-transparent px-2 pb-1.5 pt-5 text-xs font-semibold text-white">
          <Icono className="w-3.5 h-3.5" /> {formatearNumero(valor)}
          <span className="sr-only"> {etiqueta}</span>
        </span>
      )}
    </button>
  );
}

function TarjetasCargando() {
  return (
    <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-5" aria-label="Cargando los números de la cuenta">
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} className="h-[76px] animate-pulse rounded-2xl border border-neutral-200 bg-neutral-50" />
      ))}
    </div>
  );
}

function GrillaCargando() {
  return (
    <div className="mt-3 grid grid-cols-3 gap-1 sm:gap-2 md:grid-cols-4 lg:grid-cols-5" aria-label="Cargando los posts">
      {Array.from({ length: 10 }, (_, i) => (
        <div key={i} className="aspect-square animate-pulse rounded-md bg-neutral-100" />
      ))}
    </div>
  );
}

function Aviso({
  texto,
  onReintentar,
  accion,
}: {
  texto: string;
  onReintentar?: () => void;
  /** En vez de "Reintentar", cuando reintentar no lo arregla. */
  accion?: { texto: string; onClick: () => void };
}) {
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 py-3 px-4 text-sm text-amber-900 animate-ficha-entra">
      <AlertCircle className="w-4 h-4 shrink-0" />
      <span className="min-w-0 flex-1">{texto}</span>
      {(accion || onReintentar) && (
        <button type="button" onClick={accion?.onClick ?? onReintentar} className="text-sm font-medium underline">
          {accion?.texto ?? 'Reintentar'}
        </button>
      )}
    </div>
  );
}

function Vacio({ titulo, texto, accion }: { titulo: string; texto: string; accion: { texto: string; onClick: () => void } }) {
  return (
    <div className="mx-auto mt-5 max-w-lg rounded-2xl border border-neutral-200 bg-white py-12 px-6 text-center shadow-sm animate-ficha-entra">
      <span className="mx-auto grid h-12 w-12 place-items-center rounded-xl bg-gradient-to-tr from-amber-400 via-pink-500 to-purple-600 text-white">
        <Instagram className="w-6 h-6" />
      </span>
      <h2 className="mt-4 text-base font-semibold text-brand-black">{titulo}</h2>
      <p className="mt-1 text-sm text-neutral-500">{texto}</p>
      <button type="button" onClick={accion.onClick} className={boton('primario', { extra: 'mt-5' })}>
        {accion.texto}
      </button>
    </div>
  );
}
